import { Injectable } from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, switchMap } from 'rxjs';
import { ApproachFactoryService } from 'src/app/module/order/service/approach-factory.service';
import { ApproachFactoryState } from 'src/app/module/order/state/approach-factory.state';
import { OrderState } from 'src/app/module/order/state/order.state';
import { SyncService } from 'src/app/module/sync/service/sync.service';
import { SelectedSourceDataPipe } from 'src/app/shared/pipes/selected-source-data.pipe';
import { ProxyService } from 'src/app/shared/services/proxy.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { ComparableService } from '../../order/service/comparable.service';
import { PropertyState } from '../../property/state/property.state';
import { ScenarioState } from '../../scenario/state/scenario.state';
import { SyncScope } from '../../sync/interface/sync.interface';
import { ShortTermRentalApi } from '../api/short-term-rental.api';
import { ConfirmPropertyParam, RadiusSearchCriteria, ShortTermRental } from '../interface/short-term-rental.interface';
import { ShortTermRentalState } from '../state/short-term-rental.state';

@Injectable({
  providedIn: 'root',
})
export class ShortTermRentalService {
  constructor(
    private approachFactoryService: ApproachFactoryService,
    private approachFactoryState: ApproachFactoryState,
    private comparableService: ComparableService,
    private orderState: OrderState,
    private propertyState: PropertyState,
    private proxyService: ProxyService,
    private scenarioState: ScenarioState,
    private selectedSourceDataPipe: SelectedSourceDataPipe,
    private shortTermRentalApi: ShortTermRentalApi,
    private shortTermRentalState: ShortTermRentalState,
    private snackBarService: SnackBarService,
    private syncService: SyncService
  ) {
    this._listenToSubjectChanges();
  }

  getCompsByRadiusSearch(searchCriteria: RadiusSearchCriteria) {
    return this.proxyService.getCompsByRadiusSearch(searchCriteria);
  }

  confirmProperty(reqParams: ConfirmPropertyParam): Observable<boolean> {
    return this.shortTermRentalApi.confirmProperty(reqParams).pipe(
      switchMap((res) => {
        if (res?.listingId && res?.propertyId && this.orderState.activeValuationType) {
          return this.comparableService
            .fetchSelectedComparables(this.scenarioState.activeScenario!.id, this.orderState.activeValuationType)
            .pipe(
              map(() => {
                const selectedCompList = this.approachFactoryState.getOrderValue('selectedComparableList') ?? [];

                selectedCompList.push({
                  id: res.propertyId,
                  ListingId: res.listingId,
                });

                const activeScenario = structuredClone(this.scenarioState.activeScenario);

                if (activeScenario?.[this.approachFactoryState.activeApproachKey]) {
                  activeScenario[this.approachFactoryState.activeApproachKey]!.selectedComparableList = [
                    ...selectedCompList,
                  ];
                }

                this.syncService.syncLocally(SyncScope.SCENARIO, activeScenario);

                this.approachFactoryService.recalculateAndUpdateProperties();

                this.shortTermRentalState.secondarySearchFilter = {
                  ...this.shortTermRentalState.secondarySearchFilter,
                };

                this.snackBarService.open('success', 'Property identified successfully');
                return true;
              }),
              catchError(() => {
                return of(false);
              })
            );
        } else return of(false);
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  calculateIncome(data: Partial<ShortTermRental>) {
    if (data?.dailyRent == null || data?.dailyRent == undefined) {
      const activeScenario = structuredClone(this.scenarioState.activeScenario);

      if (activeScenario?.shortTermRental) {
        activeScenario.shortTermRental = {
          ...activeScenario.shortTermRental,
          occupancyRate: data?.occupancyRate,
        };
        this.scenarioState.activeScenario = activeScenario;
      }

      return;
    }

    const annualRent = Number(data?.dailyRent) * 365;
    const monthlyRent = annualRent / 12;
    const potentialGrossIncome = annualRent + Number(data?.otherRevenue ?? 0);

    const occupancyRate = Number(data?.occupancyRate);
    const nonOccupancyLoss = ((100 - occupancyRate) * potentialGrossIncome) / 100;

    const effectiveGrossIncome = Math.round(potentialGrossIncome - Number(nonOccupancyLoss));

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const derivedManagementCost =
      data?.managementCostType == 'VALUE'
        ? data?.managementCostValue
        : data?.managementCostType == 'PERCENT'
          ? Math.round((Number(data?.managementCostPercentage) * Number(effectiveGrossIncome)) / 100)
          : data?.managementCostType == 'DETAILED'
            ? Object.values(this.scenarioState.activeScenario?.shortTermRental?.managementCostDetail ?? {})?.reduce(
                (prev, cur) => Number(prev) + Number(cur),
                0
              )
            : null;

    const annualExpense =
      Number(data?.annualTax) + Number(data?.annualHoaFee) + Number(data?.insurance) + Number(derivedManagementCost);

    const netOperatingIncome = Number(effectiveGrossIncome) - Number(annualExpense);

    const activeScenario = structuredClone(this.scenarioState.activeScenario);

    if (!activeScenario?.shortTermRental) return {};

    activeScenario.shortTermRental = {
      ...activeScenario.shortTermRental,
      dailyRent: data?.dailyRent,
      otherRevenueDescription: data?.otherRevenueDescription,
      otherRevenue: data?.otherRevenue,
      potentialGrossIncome: potentialGrossIncome,
      occupancyRate: data?.occupancyRate,
      effectiveGrossIncome: effectiveGrossIncome,
      annualTax: data?.annualTax,
      annualHoaFee: data?.annualHoaFee,
      insurance: data?.insurance,
      managementCostType: data?.managementCostType,
      derivedManagementCost: derivedManagementCost,
      managementCostPercentage: data?.managementCostPercentage,
      managementCostValue: data?.managementCostValue,
      annualExpense: annualExpense,
      netOperatingIncome: netOperatingIncome,
    };

    this.scenarioState.activeScenario = activeScenario;

    return {
      monthlyRent: monthlyRent,
      annualRent: annualRent,
      potentialGrossIncome: potentialGrossIncome,
      effectiveGrossIncome: effectiveGrossIncome,
      derivedManagementCost: derivedManagementCost,
      annualExpense: annualExpense,
      netOperatingIncome: netOperatingIncome,
      nonOccupancyLoss: nonOccupancyLoss,
    };
  }

  private _listenToSubjectChanges() {
    this.propertyState.subjectPropertyData$
      .pipe(
        debounceTime(500),
        distinctUntilChanged((prev, curr) => {
          return JSON.stringify(prev?.OccupancyRate) === JSON.stringify(curr?.OccupancyRate);
        })
      )
      .subscribe((subject) => {
        this.calculateIncome({
          ...(this.scenarioState.activeScenario?.shortTermRental ?? {}),
          occupancyRate: this.selectedSourceDataPipe.transform(subject?.OccupancyRate),
        });
      });
  }
}
